import {format} from 'date-fns';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {platformLabel} from '../types/feedback';

interface AffectedScopeProps {
    platforms?: string[];
    versions?: string[];
    /** Derniere fois que le bug s'est manifeste : dernier evenement Sentry,
     *  ou dernier signalement recu. */
    lastOccurrence?: Date | null;
}

/**
 * Portee d'un candidat ou d'un bug : plateformes, versions, derniere occurrence.
 *
 * Rendu comme une suite de `<span>` a poser dans une ligne de
 * meta-informations, pas comme un bloc : la portee se lit avec le reste de la
 * provenance.
 *
 * Plateformes et versions sont toujours affichees, meme sans valeur connue :
 * « non determinees » informe, alors qu'une ligne absente laisse croire que
 * tout le monde est touche. La date, elle, est omise quand elle manque : il n'y
 * a rien a en dire.
 */
export function AffectedScope({platforms, versions, lastOccurrence}: AffectedScopeProps) {
    const t = useTraduire();
    const labels = [...new Set((platforms ?? []).map(platformLabel))];
    return (
        <>
            <span>
                <span className="text-gray-400">{t(T.plateformes)} </span>
                {labels.length > 0 ? labels.join(', ') : 'non déterminées'}
            </span>
            <span>
                <span className="text-gray-400">{t(T.versionsTouchees)} </span>
                {versions?.length
                    ? versions.map((v) => `v${v}`).join(', ')
                    : 'non déterminées'}
            </span>
            {lastOccurrence && (
                <span>
                    <span className="text-gray-400">{t(T.derniereOccurrence)} </span>
                    {format(lastOccurrence, 'dd/MM/yyyy \'à\' HH:mm')}
                </span>
            )}
        </>
    );
}

/** Date lisible par `AffectedScope`, ou null si la valeur est absente ou
 *  illisible : un « Invalid Date » affiche vaut moins que rien. */
export function toOccurrenceDate(value?: string | null): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
